import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, FileSpreadsheet, Landmark, ListFilter,
  GitMerge, Coins, RotateCcw, ChevronsLeft, ChevronsRight, Bookmark, LogOut, Activity, ShieldCheck,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';

const BASE_NAV = [
  { to: '/', label: 'Dashboards',     icon: LayoutDashboard,  group: 'Analyse', testId: 'nav-dashboards' },
  { to: '/entry', label: 'Data Entry', icon: FileSpreadsheet, group: 'Capture', testId: 'nav-entry' },
  { to: '/sources',   label: 'Source Master',   icon: Landmark,    group: 'Masters', testId: 'nav-sources' },
  { to: '/dropdowns', label: 'Dropdown Master', icon: ListFilter,  group: 'Masters', testId: 'nav-dropdowns' },
  { to: '/assets',    label: 'Asset Master',    icon: GitMerge,    group: 'Masters', testId: 'nav-assets' },
  { to: '/activity',  label: 'Activity Log',    icon: Activity,    group: 'Audit',   testId: 'nav-activity' },
];

const ADMIN_NAV_ITEM = {
  to: '/admin', label: 'Admin Console', icon: ShieldCheck, group: 'Admin', testId: 'nav-admin',
};

const STORAGE_KEY_COLLAPSE = 'sidebar-collapsed-v1';

const NavItemTooltip = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="right" className="bg-[#2B2B2B] text-white">
      {label}
    </TooltipContent>
  </Tooltip>
);

export const Sidebar = () => {
  const { resetAll, dashboardViews = [], user, logout, isAdmin } = useApp();
  const navigate = useNavigate();

  const NAV = isAdmin ? [...BASE_NAV, ADMIN_NAV_ITEM] : BASE_NAV;

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_COLLAPSE) === '1'; } catch (_) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_COLLAPSE, collapsed ? '1' : '0'); } catch (_) {}
  }, [collapsed]);

  const groups = NAV.reduce((acc, item) => {
    acc[item.group] = [...(acc[item.group] || []), item];
    return acc;
  }, {});

  const doReset = () => {
    if (confirm('Reset all data back to seed? This will clear sources, dropdowns, holdings, assets, backups and locks.')) {
      resetAll().then(() => toast.success('Workspace reset to seed data')).catch((e) => toast.error(e.message));
    }
  };

  const doLogout = async () => {
    try {
      await logout();
      window.location.replace('/login');
    } catch (e) {
      toast.error(e.message || 'Logout failed');
    }
  };

  const applyView = (v) => navigate(`/?view=${encodeURIComponent(v.id)}`);

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        className={`shrink-0 border-r border-border bg-white h-screen sticky top-0 flex flex-col transition-[width] duration-200 ${collapsed ? 'w-[60px]' : 'w-64'}`}
        data-testid="sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        <div className="px-3 pt-5 pb-4 border-b border-border flex items-center justify-between gap-2">
          {!collapsed && (
            <div className="flex items-center gap-2 pl-2">
              <div className="w-7 h-7 bg-[hsl(var(--primary))] flex items-center justify-center">
                <Coins size={16} strokeWidth={2.25} className="text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-[15px] tracking-tight leading-none">Track</div>
                <div className="overline mt-1 leading-none text-[10px]">Personal&nbsp;Wealth</div>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-7 h-7 bg-[hsl(var(--primary))] flex items-center justify-center mx-auto">
              <Coins size={16} strokeWidth={2.25} className="text-white" />
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={`text-[#575757] hover:text-[hsl(var(--primary))] p-1 rounded ${collapsed ? 'absolute right-[-12px] top-6 bg-white border border-border shadow-sm' : ''}`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-4">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              {!collapsed && <div className="overline px-3 mb-2">{group}</div>}
              <div className="space-y-0.5">
                {items.map(({ to, label, icon: Icon, testId }) => {
                  const link = (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === '/'}
                      data-testid={testId}
                      className={({ isActive }) =>
                        `group flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-sm transition-colors font-medium ${
                          collapsed ? 'justify-center' : ''
                        } ${
                          isActive
                            ? 'bg-[hsl(var(--primary))] text-white'
                            : 'text-[#2B2B2B] hover:bg-[#EFEFEA]'
                        }`
                      }
                    >
                      <Icon size={15} strokeWidth={2} />
                      {!collapsed && <span>{label}</span>}
                    </NavLink>
                  );
                  if (collapsed) {
                    return (
                      <NavItemTooltip key={to} label={label}>
                        {link}
                      </NavItemTooltip>
                    );
                  }
                  return link;
                })}

                {/* Saved views appear under "Analyse" group, below Dashboards */}
                {group === 'Analyse' && dashboardViews.length > 0 && (
                  <div className={`${collapsed ? 'mt-2 space-y-0.5' : 'mt-1 ml-7 pl-2 border-l border-border space-y-0.5'}`}>
                    {!collapsed && (
                      <div className="overline text-[9px] text-[#787872] mb-1 mt-1 flex items-center gap-1">
                        <Bookmark size={9} /> Saved views
                      </div>
                    )}
                    {dashboardViews.map((v) => {
                      const btn = (
                        <button
                          key={v.id}
                          onClick={() => applyView(v)}
                          data-testid={`sidebar-view-${v.id}`}
                          className={`w-full text-left text-[12px] py-1 rounded-sm hover:bg-[#EFEFEA] text-[#2B2B2B] flex items-center gap-2 ${collapsed ? 'justify-center px-0' : 'px-2'}`}
                        >
                          {collapsed ? <Bookmark size={12} className="text-[#575757]" /> : (
                            <>
                              <span className="w-1 h-1 rounded-full bg-[#B0B0A8]" />
                              <span className="truncate">{v.name}</span>
                            </>
                          )}
                        </button>
                      );
                      const tooltipLabel = v.description
                        ? <div><div className="font-heading font-semibold mb-1">{v.name}</div><div className="text-[11px] opacity-90 max-w-[220px] whitespace-pre-wrap">{v.description}</div></div>
                        : v.name;
                      return (
                        <Tooltip key={v.id}>
                          <TooltipTrigger asChild>{btn}</TooltipTrigger>
                          <TooltipContent side="right" className="bg-[#2B2B2B] text-white">
                            {tooltipLabel}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </nav>

        <div className={`border-t border-border ${collapsed ? 'px-2 py-3 space-y-2' : 'px-4 py-4 space-y-3'}`}>
          {!collapsed && (
            <>
              <div>
                <div className="overline mb-2">Display currency</div>
                <div className="border border-border rounded-sm px-3 py-2 bg-[#F6F6F4] flex items-center justify-between" data-testid="currency-indicator">
                  <span className="font-heading font-semibold text-sm">₹ INR</span>
                  <span className="text-[10px] font-mono text-[#787872]">Indian Rupee</span>
                </div>
              </div>
              {user && (
                <div className="border border-border rounded-sm px-3 py-2 bg-white flex items-center gap-2" data-testid="user-card">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-7 h-7 bg-[#F1F6F2] flex items-center justify-center font-heading text-[12px] font-bold text-[hsl(var(--primary))]">
                      {(user.name || user.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-[12px] truncate">{user.name || user.email}</div>
                    <div className="text-[10px] font-mono text-[#787872] truncate">{user.email}</div>
                  </div>
                  <button
                    onClick={doLogout}
                    className="text-[#787872] hover:text-[#9B2C2C] p-1"
                    title="Logout"
                    data-testid="sidebar-logout"
                  >
                    <LogOut size={13} />
                  </button>
                </div>
              )}
              <button
                onClick={doReset}
                className="w-full text-[11px] text-[#787872] hover:text-[#9B2C2C] font-mono text-left flex items-center gap-1.5 transition-colors"
                data-testid="sidebar-reset-link"
              >
                <RotateCcw size={11} /> Reset demo data
              </button>
              <div className="text-[11px] text-[#787872] font-mono leading-snug">
                v1.0 · Personal use<br/>
                Synced to your Google account
              </div>
            </>
          )}
          {collapsed && (
            <div className="space-y-2">
              {user && (
                <NavItemTooltip label={`${user.name || user.email} · Logout`}>
                  <button
                    onClick={doLogout}
                    className="w-full p-2 text-[#787872] hover:text-[hsl(var(--primary))] flex items-center justify-center"
                    data-testid="sidebar-logout"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <LogOut size={13} />
                    )}
                  </button>
                </NavItemTooltip>
              )}
              <NavItemTooltip label="Reset demo data">
                <button
                  onClick={doReset}
                  className="w-full p-2 text-[#787872] hover:text-[#9B2C2C] flex items-center justify-center"
                  data-testid="sidebar-reset-link"
                >
                  <RotateCcw size={13} />
                </button>
              </NavItemTooltip>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
